import React, {useRef, useState, useCallback} from 'react'
import {Box, IconButton} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getGDIHideStyles, hasGDIViewTypes} from '../../utils/gdi-styles'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import GDILinkBlock from '../gdiLinkBlock'
import {Mute, Pause, Play, Unmute} from '../../../../icons'
import styles from './styles'

const hasAuthoredValue = (value) => value !== undefined && value !== null && value !== ''

const parseCmsBoolean = (value, fallback) => {
    if (!hasAuthoredValue(value)) return fallback
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true
        if (['false', '0', 'no', 'off'].includes(normalized)) return false
    }

    return fallback
}

const getFirstAuthoredValue = (source, keys) => {
    for (const key of keys) {
        if (hasAuthoredValue(source?.[key])) return source[key]
    }

    return undefined
}

const resolveMediaUrl = (ref) => ref?.Url || ref?.Expanded?.Url || null

const getDefaultStylesEntry = (stylesPerViewType) => {
    if (!Array.isArray(stylesPerViewType) || stylesPerViewType.length === 0) return {}
    return stylesPerViewType.find((entry) => !hasGDIViewTypes(entry)) || stylesPerViewType[0] || {}
}

const GDIVideoBlock = ({
    HideBlockOn,
    VideoExternalUrl,
    VideoExternalUrlTablet,
    VideoExternalUrlDesktop,
    AriaLabelText,
    PosterImage,
    Link,
    GDIVideoBlockLink,
    Caption,
    GDIVideoBlockStylesPerViewType,
    ...props
}) => {
    const hideStyles = getGDIHideStyles(HideBlockOn)

    const videoRefs = useRef([])

    // Video controls are global; prefer the non-view-type default entry when present.
    const defaultStyles = getDefaultStylesEntry(GDIVideoBlockStylesPerViewType)
    const autoPlay = parseCmsBoolean(defaultStyles.AutoPlay, true)
    const loop = parseCmsBoolean(defaultStyles.Loop, true)
    const authoredMute = getFirstAuthoredValue(defaultStyles, ['MuteVideo'])
    const muteVideo = hasAuthoredValue(authoredMute)
        ? parseCmsBoolean(authoredMute, false)
        : autoPlay
    const hideMuteIcon = parseCmsBoolean(defaultStyles.HideMuteIcon, false)
    const hidePlayPauseIcon = parseCmsBoolean(
        getFirstAuthoredValue(defaultStyles, [
            'HidePlayPauseIcon',
            'GDIVideoBlockHidePlayPauseIcon'
        ]),
        false
    )

    const [isPlaying, setIsPlaying] = useState(autoPlay)
    const [isMuted, setIsMuted] = useState(muteVideo)

    const posterBlock = Array.isArray(PosterImage)
        ? getExpandedFromContentArea(PosterImage)
        : PosterImage?.Expanded || PosterImage
    const posterMobileUrl = resolveMediaUrl(posterBlock?.Image) || resolveMediaUrl(posterBlock)
    const posterTabletUrl = resolveMediaUrl(posterBlock?.ImageTablet) || posterMobileUrl
    const posterDesktopUrl = resolveMediaUrl(posterBlock?.ImageDesktop) || posterTabletUrl

    const captionBlock = Caption?.Expanded

    const setVideoRef = useCallback(
        (index) => (node) => {
            videoRefs.current[index] = node
            if (node) node.muted = isMuted
        },
        [isMuted]
    )

    const getVisibleVideo = useCallback(() => {
        const videos = videoRefs.current.filter(Boolean)
        if (videos.length === 0) return null

        return (
            videos.find((video) => {
                const wrapper = video.parentElement
                if (!wrapper || typeof window === 'undefined') return false
                return window.getComputedStyle(wrapper).display !== 'none'
            }) || videos[0]
        )
    }, [])

    const togglePlayPause = useCallback(
        (event) => {
            event?.preventDefault()
            event?.stopPropagation()

            const video = getVisibleVideo()
            if (!video) return
            if (video.paused) {
                const playPromise = video.play()
                setIsPlaying(true)
                playPromise?.catch?.(() => setIsPlaying(false))
            } else {
                video.pause()
                setIsPlaying(false)
            }
        },
        [getVisibleVideo]
    )

    const toggleMute = useCallback(
        (event) => {
            event?.preventDefault()
            event?.stopPropagation()

            const nextMuted = !isMuted
            videoRefs.current.forEach((video) => {
                if (video) video.muted = nextMuted
            })
            setIsMuted(nextMuted)
        },
        [isMuted]
    )

    if (hideStyles.doNotLoadComponent) return null
    if (!VideoExternalUrl) return null

    const linkBlock = getExpandedFromContentArea(GDIVideoBlockLink || Link)

    const videoElement = (
        <Box
            data-block-type="GDIVideoBlock"
            sx={hideStyles.sx}
            {...styles.root}
            {...props}
        >
            {/* Responsive video sources */}
            <Box
                {...styles.responsiveVideoWrapper}
                display={{
                    base: 'block',
                    md: VideoExternalUrlTablet ? 'none' : 'block',
                    lg: VideoExternalUrlDesktop ? 'none' : VideoExternalUrlTablet ? 'none' : 'block'
                }}
            >
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                    ref={setVideoRef(0)}
                    src={VideoExternalUrl}
                    poster={posterMobileUrl}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted={isMuted}
                    playsInline
                    aria-label={AriaLabelText}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    style={styles.coverVideo}
                />
            </Box>

            {VideoExternalUrlTablet && (
                <Box
                    {...styles.responsiveVideoWrapper}
                    display={{
                        base: 'none',
                        md: 'block',
                        lg: VideoExternalUrlDesktop ? 'none' : 'block'
                    }}
                >
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                        ref={setVideoRef(1)}
                        src={VideoExternalUrlTablet}
                        poster={posterTabletUrl}
                        autoPlay={autoPlay}
                        loop={loop}
                        muted={isMuted}
                        playsInline
                        aria-label={AriaLabelText}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        style={styles.coverVideo}
                    />
                </Box>
            )}

            {VideoExternalUrlDesktop && (
                <Box {...styles.responsiveVideoWrapper} display={{base: 'none', lg: 'block'}}>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                        ref={setVideoRef(2)}
                        src={VideoExternalUrlDesktop}
                        poster={posterDesktopUrl}
                        autoPlay={autoPlay}
                        loop={loop}
                        muted={isMuted}
                        playsInline
                        aria-label={AriaLabelText}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        style={styles.coverVideo}
                    />
                </Box>
            )}

            {(!hidePlayPauseIcon || !hideMuteIcon) && (
                <Box {...styles.controls}>
                    {!hidePlayPauseIcon && (
                        <IconButton
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            onClick={togglePlayPause}
                            {...styles.controlButton}
                            icon={isPlaying ? <Pause /> : <Play />}
                        />
                    )}

                    {!hideMuteIcon && (
                        <IconButton
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                            onClick={toggleMute}
                            {...styles.controlButton}
                            icon={isMuted ? <Unmute /> : <Mute />}
                        />
                    )}
                </Box>
            )}

            {captionBlock && (
                <Box
                    {...styles.caption}
                    dangerouslySetInnerHTML={{__html: captionBlock.TextForWeb || ''}}
                />
            )}
        </Box>
    )

    if (linkBlock) {
        return <GDILinkBlock {...linkBlock}>{videoElement}</GDILinkBlock>
    }

    return videoElement
}

GDIVideoBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    VideoExternalUrl: PropTypes.string,
    VideoExternalUrlTablet: PropTypes.string,
    VideoExternalUrlDesktop: PropTypes.string,
    AriaLabelText: PropTypes.string,
    PosterImage: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({Expanded: PropTypes.object})
    ]),
    Link: PropTypes.array,
    GDIVideoBlockLink: PropTypes.array,
    Caption: PropTypes.shape({Expanded: PropTypes.object}),
    GDIVideoBlockStylesPerViewType: PropTypes.array
}

export default GDIVideoBlock
